import { useState, useEffect } from 'react'
import { Bookmark, X } from 'lucide-react'
import { useSavedArticlesList, useToggleSavedArticle } from '../api/hooks'
import { ArticleListView } from '../components/mywire/ArticleListView'
import type { WireArticle } from '../api/types'

export default function SavedArticlesPage() {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useSavedArticlesList()
  const toggleSaved = useToggleSavedArticle()
  const [removedArticles, setRemovedArticles] = useState<WireArticle[]>([])

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  function handleRemove(article: WireArticle) {
    setRemovedArticles((prev) => [...prev, article])
    toggleSaved.mutate({ article, nextSaved: false })
  }

  function handleUndo(article: WireArticle) {
    setRemovedArticles((prev) => prev.filter((a) => a.id !== article.id))
    toggleSaved.mutate({ article, nextSaved: true })
  }

  function handleDismiss() {
    setRemovedArticles([])
  }

  const alert = removedArticles.length > 0 ? (
    <div className="mb-4 flex items-start justify-between gap-2 rounded-md border border-line bg-fill px-3 py-2.5">
      <div className="space-y-1">
        {removedArticles.map((article) => (
          <p key={article.id} className="text-xs text-fg-secondary">
            <span className="font-medium text-fg">{article.title}</span> was removed.{' '}
            <button
              type="button"
              onClick={() => handleUndo(article)}
              className="font-semibold text-accent-primary-fg underline hover:no-underline"
            >
              Undo
            </button>
          </p>
        ))}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
        className="shrink-0 rounded p-0.5 text-fg-secondary transition-colors hover:text-fg"
      >
        <X size={13} aria-hidden="true" />
      </button>
    </div>
  ) : undefined

  return (
    <ArticleListView
      articles={data?.pages.flatMap((p) => p.items) ?? []}
      isLoading={isLoading}
      isFullyLoaded={!isLoading && !hasNextPage && !isFetchingNextPage}
      title="Saved Articles"
      icon={Bookmark}
      emptyTitle="No saved articles yet."
      emptySubtitle="Bookmark articles as you read them and they'll show up here."
      alert={alert}
      onRemove={handleRemove}
    />
  )
}
